import axios, { AxiosError } from "axios";
import { Provider, ContactFormSubmission } from "../types";

/**
 * Provider Service
 *
 * API service for fetching provider data and handling contact form submissions.
 * These endpoints are public and don't require authentication.
 */

// API base URL - will need to be added to .env files
// REACT_APP_AWS_PROVIDER_API_URL=https://your-api-gateway-url.execute-api.us-east-1.amazonaws.com/prod/providers
const PROVIDER_API_URL =
  process.env.REACT_APP_AWS_PROVIDER_API_URL || "/api/providers";

const CONTACT_API_URL =
  process.env.REACT_APP_AWS_CONTACT_API_URL || "/api/contact";

/**
 * Fetch a provider by username
 * Public endpoint - no authentication required
 *
 * @param username - URL-friendly username (e.g., "john-smith")
 * @returns Provider data or null if not found
 */
export async function getProviderByUsername(
  username: string
): Promise<Provider | null> {
  try {
    const response = await axios.get(`${PROVIDER_API_URL}/${username}`, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 10000,
    });

    const providerData = response.data;

    // Parse JSON strings from DynamoDB if needed
    if (typeof providerData.services === "string") {
      providerData.services = JSON.parse(providerData.services);
    }
    if (typeof providerData.socialLinks === "string") {
      providerData.socialLinks = JSON.parse(providerData.socialLinks);
    }
    if (typeof providerData.seo === "string") {
      providerData.seo = JSON.parse(providerData.seo);
    }

    return providerData as Provider;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        console.log(`Provider not found: ${username}`);
        return null;
      }
      console.error("Error fetching provider:", error.response?.data);
    } else {
      console.error("Unexpected error fetching provider:", error);
    }
    throw error;
  }
}

/**
 * Fetch all active providers
 * Public endpoint - no authentication required
 * Useful for listing all providers or creating a directory page
 *
 * @returns Array of active providers
 */
export async function getAllActiveProviders(): Promise<Provider[]> {
  try {
    const response = await axios.get(`${PROVIDER_API_URL}`, {
      headers: {
        "Content-Type": "application/json",
      },
      timeout: 10000,
    });

    const providers = response.data;

    // Parse JSON strings for each provider
    return providers.map((provider: any) => {
      if (typeof provider.services === "string") {
        provider.services = JSON.parse(provider.services);
      }
      if (typeof provider.socialLinks === "string") {
        provider.socialLinks = JSON.parse(provider.socialLinks);
      }
      if (typeof provider.seo === "string") {
        provider.seo = JSON.parse(provider.seo);
      }
      return provider as Provider;
    });
  } catch (error) {
    console.error("Error fetching providers:", error);
    throw error;
  }
}

/**
 * Submit a contact form
 * Public endpoint - no authentication required
 * Sends email to provider via AWS SES
 *
 * @param submission - Contact form data
 * @returns Success status
 */
export async function submitContactForm(
  submission: ContactFormSubmission
): Promise<{ success: boolean; message: string }> {
  try {
    const response = await axios.post(
      CONTACT_API_URL,
      {
        ...submission,
        submittedAt: new Date().toISOString(),
      },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 15000, // Longer timeout for email sending
      }
    );

    return {
      success: true,
      message: response.data.message || "Message sent successfully!",
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message =
        error.response?.data?.message ||
        "Failed to send message. Please try again.";
      console.error("Error submitting contact form:", error.response?.data);
      return {
        success: false,
        message,
      };
    }
    console.error("Unexpected error submitting contact form:", error);
    return {
      success: false,
      message: "An unexpected error occurred. Please try again later.",
    };
  }
}

/**
 * Mock data for development/testing
 * Use this when the backend API is not yet available
 */
export function getMockProvider(username: string): Provider {
  return {
    id: "mock-id-001",
    username: username,
    name: "Rhett Guyer",
    email: "admin@wheresour.com",
    location: {
      city: "Charlotte",
      state: "NC",
      zipCode: "28262",
      serviceAreas: ["Charlotte", "Concord", "Kannapolis", "China Grove", "Salisbury", "High Point", "Greensboro", "Winston Salem", "Lexington"],
      latitude: 35.344114,
      longitude: -80.700227,
    },
    headline: "Professional Organizer & Inventory Management Expert",
    bio: `Hi, I'm Rhett, and I believe everyone deserves a space that works as hard as they do.

As a software engineer with a passion for organization, I founded WheresOur to bridge the gap between traditional decluttering services and modern technology. After years of helping friends and family transform their chaotic spaces into functional systems, I realized the biggest challenge wasn't just organizing—it was maintaining that organization over time.

That's when I developed my proprietary inventory tracking application. This tool doesn't just help you get organized; it ensures you stay organized by putting the location of every item at your fingertips. No more buying duplicates of things you already own or spending hours searching for important items.

My background in software development means I approach each organizing project with a systems mindset—creating logical, sustainable solutions that make sense for how you actually live and work. My hands-on experience in carpentry and space planning allows me to offer complete solutions, from custom shelving installations to efficient storage systems tailored to your specific needs.

I understand that letting someone into your space and your life requires trust. That's why I approach every project with discretion, respect, and zero judgment. Whether you're a busy professional who needs to reclaim your home office, preparing for a major life transition, or simply ready to take control of your space, I'm here to make the process as smooth and stress-free as possible.

Ready to get started? Let's create a space that supports your best life.`,
    profileImageUrl: "https://wheresour.s3.us-east-1.amazonaws.com/user_images/RhettProfile.jpg",
    services: [
      {
        id: "service-1",
        title: "Home Organization",
        description:
          "Complete home organization services including closets, kitchens, garages, and storage spaces.",
        features: [
          "Custom organization systems",
          "Decluttering assistance",
          "Space planning and optimization",
          "Ongoing maintenance support",
          "Trash and donation removal"
        ],
        pricing: "Starting at $50/hour",
        modalContent: {
          detailedDescription: "Transform your home into an organized, functional space where everything has its place. I work with you to create customized systems that match your lifestyle, not force you into a one-size-fits-all solution. Whether you're overwhelmed by clutter, preparing for a move, or just want to make daily life easier, I'll help you create lasting change.",
          process: [
            "Initial consultation to understand your goals and pain points",
            "Assessment of your space and current organization challenges",
            "Custom organization plan tailored to your needs and budget",
            "Hands-on organizing session with sorting, purging, and system implementation",
            "Follow-up support to ensure systems work for your lifestyle"
          ],
          benefits: [
            "Save time finding what you need",
            "Reduce stress and anxiety from clutter",
            "Increase productivity in your home",
            "Stop buying duplicates of items you already own",
            "Create a peaceful, functional living environment"
          ],
          idealFor: [
            "Busy professionals who need efficient systems",
            "Anyone preparing to sell their home",
            "People who've tried organizing but can't maintain it",
            "Those who feel overwhelmed and don't know where to start"
          ],
        }
      },
      {
        id: "service-4",
        title: "Inventory Management",
        description:
          "Digital inventory solutions for homes, businesses, and collectors.",
        features: [
          "Complete item cataloging",
          "Photo documentation",
          "Digital organization system",
          "Insurance documentation",
        ],
        pricing: "Starting at $50/hour",
        modalContent: {
          detailedDescription: "Never wonder where something is again. Using my proprietary WheresOur app, I create a comprehensive digital inventory of your belongings with photos and exact locations. Whether you need insurance documentation, want to track valuable collections, or simply want to know what you own and where it is, I'll build a searchable database that puts every item at your fingertips.",
          process: [
            "Consultation to determine scope and prioritize areas/items to catalog",
            "Systematic room-by-room or collection-by-collection inventory",
            "Photo documentation of each item or group of items",
            "Data entry into the WheresOur app with location tracking",
            "Organization of physical items alongside digital cataloging",
            "Training on how to use and maintain your digital inventory",
            "Delivery of comprehensive reports for insurance or estate planning"
          ],
          benefits: [
            "Instantly locate any item in your home with a quick search",
            "Never buy duplicates of things you forgot you owned",
            "Complete documentation for insurance claims if needed",
            "Peace of mind knowing exactly what you have and where",
            "Simplified estate planning with detailed asset records",
            "Track collections and appraisals in one organized system",
            "Share inventory access with family members if desired"
          ],
          idealFor: [
            "Homeowners with valuable items needing insurance documentation",
            "Collectors who want to track and organize their collections",
            "Families doing estate planning or managing inherited items",
            "Business owners tracking inventory or equipment",
            "Anyone moving who wants to know exactly what they're packing",
            "People with storage units or multiple properties",
            "Those who frequently misplace items or forget what they own"
          ],
        }
      },
      {
        id: "service-2",
        title: "Senior Downsizing & Transition Services",
        description:
          "Compassionate support for seniors and during downsizing and estate transitions. This is an emotional process, and I'm here to make it as stress-free as possible.",
        features: [
          "Gentle, patient approach with seniors",
          "Lifetime possession sorting and decision support",
          "Donation and estate sale organization",
          "Digital cataloging of sentimental items and heirlooms",
          "New space setup and organization",
          "Ongoing support after the transition"
        ],
        pricing: "Starting at $50/hour",
        modalContent: {
          detailedDescription: "Downsizing is about more than just getting rid of things—it's about honoring a lifetime of memories while creating a comfortable new chapter. I provide compassionate, patient support for seniors and their families during this emotional transition. Whether moving to a smaller home, assisted living, or managing an estate, I handle every item with care and respect, making decisions easier and the process less overwhelming.",
          process: [
            "Gentle initial meeting to understand goals, timeline, and emotional needs",
            "Collaborate with family members to align on priorities and approach",
            "Thoughtful sorting of lifetime possessions with patience and understanding",
            "Digital documentation of sentimental items and family heirlooms",
            "Coordination of donations, estate sales, or family distribution",
            "Professional packing and move coordination if needed",
            "New space setup to feel like home from day one",
            "Follow-up visits to ensure comfort and make adjustments"
          ],
          benefits: [
            "Reduce emotional stress during a difficult transition",
            "Preserve family harmony by handling sensitive decisions professionally",
            "Ensure treasured items go to the right family members",
            "Save time and energy during an already overwhelming period",
            "Create a safe, organized new living space",
            "Maximize value from items through proper donation/sale",
            "Maintain dignity and respect throughout the process"
          ],
          idealFor: [
            "Seniors transitioning to smaller homes or assisted living",
            "Adult children helping aging parents downsize",
            "Families managing estate transitions after a loss",
            "Anyone overwhelmed by decades of accumulated possessions",
            "People who want professional support for emotional decisions",
            "Families spread across different cities needing coordination help",
            "Those wanting to preserve family history while simplifying"
          ],
        }
      },
      {
        id: "service-3",
        title: "Storage Unit Liberation & Space Optimization",
        description:
          "Stop paying for storage! I help you reclaim space in your home and eliminate expensive storage units by creating efficient systems that make the most of what you already have.",
        features: [
          "Storage unit inventory and assessment",
          "Strategic space planning to fit belongings in your home",
          "Custom storage solutions and shelving recommendations",
          "Decluttering and donation coordination",
          "Cost-benefit analysis of keeping vs. storing items",
          "Garage, basement, and attic transformation",
          "Vertical space maximization strategies",
          "One-time or phased storage unit elimination"
        ],
        pricing: "Starting at $50/hour",
        modalContent: {
          detailedDescription: "Are you paying $100+ every month for a storage unit filled with things you rarely access? Let's put that money back in your pocket. I help you eliminate expensive storage units by maximizing your existing home space—garages, basements, attics, and closets. Through strategic planning and smart organization, most people discover they can fit everything they truly need right at home, saving thousands of dollars annually.",
          process: [
            "Storage unit inventory to see exactly what you're paying to store",
            "Assessment of available home space (garage, basement, attic, closets)",
            "Cost-benefit analysis: monthly fees vs. value of items stored",
            "Strategic decisions on what to keep, donate, sell, or discard",
            "Custom storage solution design for your home spaces",
            "Recommendations for shelving, bins, and organizational systems",
            "Coordinated move from storage unit to optimized home spaces",
            "Final unit cleanup and cancellation support"
          ],
          benefits: [
            "Save $1,200-$2,400+ annually in storage fees",
            "Have your belongings accessible at home, not miles away",
            "Maximize vertical space you didn't know you had",
            "Transform garages, basements, and attics into functional storage",
            "Stop paying to store things you forgot you owned",
            "One-time investment that pays for itself in a few months",
            "Reclaim mental energy from managing off-site storage"
          ],
          idealFor: [
            "Anyone paying monthly storage unit fees",
            "People who rarely visit their storage unit",
            "Homeowners with underutilized garages or basements",
            "Those who inherited items and don't know what to do with them",
            "Families who've outgrown their storage solutions",
            "People who want their belongings accessible, not in storage",
            "Anyone ready to stop throwing money away on monthly fees"
          ],
        }
      },
    ],
    acceptsContactForm: true,
    preferredContactMethod: "form",
    seo: {
      metaTitle: "Professional Organizer Charlotte NC | Home Organization & Senior Downsizing",
      metaDescription:
        "Expert home organization, senior downsizing, and storage solutions serving Charlotte, Concord, Kannapolis & surrounding NC areas. Stop paying for storage units - optimize your space today!",
      keywords: [
        "professional organizer Charlotte NC",
        "home organization Charlotte",
        "senior downsizing Charlotte",
        "storage unit elimination",
        "decluttering services Concord NC",
        "space optimization Kannapolis",
        "inventory management Charlotte",
        "organizer near me Charlotte",
        "Charlotte NC organizer",
        "North Carolina professional organizer",
        "Salisbury NC organization services",
        "Winston Salem organizer",
      ],
    },
    isActive: true,
    featured: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}
